import { html } from '@beunity/lit-components';
import BaseMenu from '@beunity/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import '@beunity/lit-components/components/dot_menu/element.js';

import ChatModel from '@beunity/lit-components/components/chat/lib/chat.model.js';

import { StoreController } from '@beunity/lit-components/lib/lit/store_controller.js';
import { computedChatsStore } from '@beunity/lit-components/components/chat/lib/chats.store.js';

import { uuid } from '@beunity/lit-components/lib/helpers.js';

/**
 * The dot-dot-dot menu for chat
 *
 * @prop resourceName - Menu title
 * @prop chatId - The id of the chat
 */

export default class ChatMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    chatId: { type: Number },
  };

  storeController = new StoreController(this, computedChatsStore);

  connectedCallback() {
    super.connectedCallback();
    this.chatModel = new ChatModel(this.chatId);
    this.resourceName = this.chatModel.storedChat?.name;
  }

  get isMuted() {
    return this.chatModel.storedChat?.isMuted;
  }

  get isUnread() {
    return (
      this.chatModel.storedChat?.unreadMessagesCount ||
      this.chatModel.storedChat?.isMarkedAsUnread
    );
  }

  get kind() {
    return this.chatModel.storedChat?.kind;
  }

  get subkind() {
    return this.chatModel.storedChat?.subkind;
  }

  get links() {
    return this.chatModel.storedChat?.links || {};
  }

  /** @internal */
  get instanceId() {
    return (this.uuid ??= uuid());
  }

  /** @internal */
  get baseUrl() {
    return `/chats/${this.chatId}`;
  }

  get exitOn() {
    return this.kind !== 'group';
  }

  get muteItem() {
    return {
      title: this.isMuted ? this.t('actions.unmute') : this.t('actions.mute'),
      action: this.actionFor('toggleMute'),
      icon: 'volume',
      partName: 'volume',
    };
  }

  get exitItem() {
    return {
      title: this.t('actions.exit'),
      action: this.actionFor('exitChat'),
      role: 'destructive',
      icon: 'close',
      partName: 'delete destructive',
    };
  }

  get toggleUnreadItem() {
    return {
      title: this.isUnread
        ? this.t('actions.mark_as_read')
        : this.t('actions.mark_as_unread'),
      action: this.actionFor('toggleUnread'),
      icon: this.isUnread ? 'envelope-open' : 'envelope',
      partName: 'check',
    };
  }

  get seeMembersItem() {
    if (!this.links.members) return;

    return {
      title: this.t('actions.see_members'),
      action: this.actionFor('visit', [this.links.members]),
      icon: 'users',
      partName: 'users',
    };
  }

  get addUsersItem() {
    if (!this.links.add_users) return;

    return {
      title: this.t('actions.add_users'),
      action: this.actionFor('execJSFromResponse', [this.links.add_users]),
      icon: 'plus-circle',
    };
  }

  get editItem() {
    if (!this.links.edit) return;

    return {
      title: this.t('actions.edit'),
      action: this.actionFor('execJSFromResponse', [this.links.edit]),
      icon: 'pencil',
    };
  }

  get backToCommunityItem() {
    if (!this.links.unit) return;

    return {
      title: this.t(`actions.back_to_community`),
      action: this.actionFor('visit', [this.links.unit]),
      icon: 'home',
      partName: 'home',
    };
  }

  get backToGroupItem() {
    if (!this.links.group) return;

    return {
      title: this.t(`actions.back_to_group`),
      action: this.actionFor('visit', [this.links.group]),
      icon: 'home',
      partName: 'home',
    };
  }

  toggleMute() {
    this.chatModel.toggleMute();
  }

  toggleUnread() {
    this.isUnread ? this.chatModel.markAsRead() : this.chatModel.markAsUnread();
  }

  visit(url) {
    this.commonActions.visit(url);
  }

  async exitChat() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) {
      return;
    }

    await this.chatModel.exit();
    const event = new Event('chatMenu-exited', {
      bubbles: true,
      composed: true,
    });
    event.detail = {
      chatId: this.chatModel.id,
    };

    this.dispatchEvent(event);
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return this.exitOn;
  }

  /** @internal */
  get items() {
    return [
      this.backToCommunityItem,
      this.backToGroupItem,
      this.toggleUnreadItem,
      this.seeMembersItem,
      this.addUsersItem,
      this.editItem,
      this.muteItem,
      this.dividerOn && this.divider,
      this.exitOn && this.exitItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      header: this.resourceName,
      buttons: this.items,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-chat-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  execJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  render() {
    return html` <ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu> `;
  }
}

ChatMenu.registerTagName('bu-chat-menu');
ChatMenu.registerCommonActions('bu-chat-menu');

customElements.get('bu-chat-menu') ||
  customElements.define('bu-chat-menu', ChatMenu);
